import { datadogLogs } from '@datadog/browser-logs';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';

import { AuthFlags } from 'state/auth/types';
import { getErrorMessage } from 'utils/datadog';

import {
  USER_POOL_ID,
  USER_POOL_CLIENT_ID,
  USER_POOL_ENDPOINT,
  OKTA_AUTH_OPTIONS,
} from '../../constants';

export const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
  endpoint: USER_POOL_ENDPOINT,
});

Amplify.configure(OKTA_AUTH_OPTIONS as ResourcesConfig);

let userToSignIn: CognitoUser | null = null;

////////////////////////////////////////////////////////////
// Sign In
////////////////////////////////////////////////////////////

export const signIn = ({
  username,
  password,
}: {
  username: string;
  password: string;
}): Promise<CognitoUserSession | AuthFlags> => {
  const normalizedUsername = normalizeString(username);
  userToSignIn = getCognitoUserForUsername(normalizedUsername);

  return new Promise((resolve, reject) =>
    userToSignIn?.authenticateUser(
      new AuthenticationDetails({
        Username: normalizedUsername,
        Password: password,
      }),
      {
        onSuccess: (session) => resolve(session),

        onFailure: (error) => {
          datadogLogs.logger.error('Sign-in failed', { error: getErrorMessage(error) });
          return reject(error);
        },

        newPasswordRequired: (userAttributes) =>
          resolve({ ...userAttributes, shouldChangePassword: true }),

        totpRequired: () => resolve({ totpRequired: true }),

        mfaSetup: () => mfaSetupCallback(resolve, reject),
      },
    ),
  );
};

export const verifyTokenForAssociation = (token: string): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) =>
    userToSignIn?.verifySoftwareToken(token, '', {
      onSuccess: (data) => resolve(data),

      onFailure: (error) => reject(error),
    }),
  );

export const verifyTokenForSignIn = (token: string): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) =>
    userToSignIn?.sendMFACode(
      token,
      {
        onSuccess: (data) => resolve(data),

        onFailure: (error) => reject(error),
      },
      'SOFTWARE_TOKEN_MFA',
    ),
  );

export const completeNewPasswordChallenge = ({ newPassword }: { newPassword: string }) =>
  new Promise((resolve, reject) => {
    userToSignIn?.completeNewPasswordChallenge(newPassword, null, {
      onSuccess: (data) => resolve(data),

      onFailure: (error) => {
        datadogLogs.logger.error('Password change failed', { error: getErrorMessage(error) });
        return reject(error);
      },

      mfaSetup: () => mfaSetupCallback(resolve, reject),
    });
  });

////////////////////////////////////////////////////////////
// Sign Out
////////////////////////////////////////////////////////////
export const signOut = (shouldSignOutGlobally = false) => {
  return new Promise<boolean>(async (resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      try {
        await fetchAuthSession();
        await amplifySignOut({ global: shouldSignOutGlobally });
        return resolve(true);
      } catch (error) {
        reject(error);
      }
      return resolve(false);
    }
    if (shouldSignOutGlobally) {
      cognitoUser?.globalSignOut({
        onSuccess: (_msg: string) => {
          resolve(true);
        },
        onFailure: (error) => {
          datadogLogs.logger.error('Global sign-out failed', { error: getErrorMessage(error) });
          reject(error);
        },
      });
    } else {
      cognitoUser?.signOut(() => {
        resolve(true);
      });
    }
  });
};

////////////////////////////////////////////////////////////
// Reset Password
////////////////////////////////////////////////////////////

export const getResetPasswordCode = ({ username }: { username: string }) =>
  new Promise<void>((resolve, reject) => {
    const currentUser = getCognitoUserForUsername(username);

    currentUser.forgotPassword({
      onSuccess: resolve,
      onFailure: (error) => {
        datadogLogs.logger.error('Failed to get password reset verification code', {
          error: getErrorMessage(error),
        });
        return reject(error);
      },
    });
  });

export const resetPassword = ({
  username,
  verificationCode,
  newPassword,
}: {
  username: string;
  verificationCode: string;
  newPassword: string;
}) =>
  new Promise<void>((resolve, reject) => {
    const currentUser = getCognitoUserForUsername(username);

    currentUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (error) => {
        datadogLogs.logger.error('Password reset failed', { error: getErrorMessage(error) });
        return reject(error);
      },
    });
  });

////////////////////////////////////////////////////////////
// Helpers
////////////////////////////////////////////////////////////

const getCognitoUserForUsername = (username: string) => {
  const normalizedUsername = normalizeString(username);
  return new CognitoUser({ Username: normalizedUsername, Pool: userPool });
};

const mfaSetupCallback = (resolve: any, reject: any) => {
  userToSignIn?.associateSoftwareToken({
    associateSecretCode: (totpSecret) =>
      resolve({
        totpSecret,
      }),

    onFailure: (error) => {
      datadogLogs.logger.error('MFA software token association failed', {
        error: getErrorMessage(error),
      });
      return reject(error);
    },
  });
};

const normalizeString = (str = '') => str.trim().toLowerCase();
